import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Search,
  Trash,
  SquareArrowOutUpRight,
  Users,
  PencilLine,
  X,
} from "lucide-react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

import ConfirmPopup from "../components/ConfirmPopup";
import PaginationComponent from "../components/PaginationComponent";
import Loading from "../components/common/LoaderCommon";
import Permission from "../components/auth/Permission";
import {
  useDelCustomer,
  useInfiniteGetAllCustomer,
  useUpdateCustomer,
} from "../service/customerService";
import useCustomerRequestStore from "../store/useCustomer";

function Customer() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { setCurrentCustomer } = useCustomerRequestStore();
  const [deleteId, setDeleteId] = useState<string | undefined>(undefined);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [searchKey, setSearchKey] = useState("");
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<any>(null);
  const [editForm, setEditForm] = useState({
    name: "",
    phoneNumber: "",
    email: "",
    address: "",
    trn: "",
    creditLimit: 0,
    creditPeriod: 0,
  });

  const { data, fetchNextPage, isLoading } = useInfiniteGetAllCustomer({
    customerName: searchKey,
    pageSize: 10,
    pageNo: currentPage,
  });

  const { mutate: deleteCustomer } = useDelCustomer();
  const { mutate: updateCustomer, isPending: isUpdating } = useUpdateCustomer();

  const handleEdit = (item: any) => {
    setEditingCustomer(item);
    setEditForm({
      name: item.username || "",
      phoneNumber: item.phoneNumber || "",
      email: item.email || "",
      address: item.address || "",
      trn: item.trn || "",
      creditLimit: item.creditLimit || 0,
      creditPeriod: item.creditPeriod || 0,
    });
    setEditModalOpen(true);
  };

  const handleUpdateCustomer = () => {
    if (!editingCustomer) return;
    const payload = {
      id: editingCustomer.id,
      customerId: editingCustomer.id,
      name: editForm.name,
      phoneNumber: editForm.phoneNumber,
      email: editForm.email,
      address: editForm.address,
      trn: editForm.trn,
      creditLimit: Number(editForm.creditLimit) || 0,
      creditPeriod: Number(editForm.creditPeriod) || 0,
    };
    updateCustomer(payload, {
      onSuccess: () => {
        toast.success("Customer updated successfully!");
        queryClient.invalidateQueries({
          queryKey: ["useInfiniteGetAllCustomer"],
        });
        setEditModalOpen(false);
        setEditingCustomer(null);
      },
      onError: (error: Error) => {
        toast.error(`Error updating customer: ${error.message}`);
      },
    });
  };

  const totalPages = Math.ceil((data?.pages[0]?.totalPages || 0) / 10);
  const customerData = data?.pages[0]?.customerResponse || [];
  const totalElements = data?.pages[0]?.totalElements ?? 0;

  const handleDelete = (id: string | undefined) => {
    deleteCustomer(id, {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["useInfiniteGetAllCustomer"],
        });
        toast.success("Customer deleted successfully!");
        setConfirmOpen(false);
        setDeleteId(undefined);
      },
      onError: (error: Error) => {
        toast.error(`Error deleting customer: ${error.message}`);
      },
    });
  };

  return (
    <div className="space-y-6 p-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-[12px] bg-[#EEF3FF] flex items-center justify-center">
            <Users className="w-5 h-5 text-[#1A5EF9]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#1A2B4A]">Customers</h1>
            <p className="text-sm text-[#8A9BB5]">
              View and manage your customer directory
            </p>
          </div>
        </div>
      </div>

      {/* Search card */}
      <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)] p-5">
        <div className="relative max-w-md">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A9BB5]" />
          <input
            type="text"
            placeholder="Search by customer name…"
            className="h-11 w-full rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] pl-11 pr-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
            onChange={(e) => {
              setSearchKey(e.target.value);
              setCurrentPage(0);
            }}
          />
        </div>
      </div>

      {/* Table card */}
      {isLoading ? (
        <Loading />
      ) : (
        <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)] overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-[#E8EEF9]">
            <p className="text-sm text-[#52627C]">
              <span className="font-semibold text-[#21314A]">
                {totalElements}
              </span>{" "}
              customers found
            </p>
            <PaginationComponent
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(page) => setCurrentPage(page)}
              fetchNextPage={fetchNextPage}
              fetchPreviousPage={() => {
                if (currentPage > 0) setCurrentPage(currentPage - 1);
              }}
            />
          </div>

          {customerData.length === 0 ? (
            <div className="py-16 text-center">
              <Users className="w-10 h-10 text-[#D0DAEE] mx-auto mb-3" />
              <p className="text-sm text-[#52627C]">No customers found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#F5F8FD] text-left text-xs font-semibold text-[#31425E] uppercase tracking-wide">
                    <th className="px-6 py-3">Name</th>
                    <th className="px-6 py-3">Email</th>
                    <th className="px-6 py-3">Phone</th>
                    <th className="px-6 py-3">Address</th>
                    <th className="px-6 py-3 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {customerData.map((item: any) => (
                    <tr
                      key={item.id}
                      className="border-t border-[#EEF2FA] text-sm text-[#43536A] hover:bg-[#FAFCFF] transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-[#EEF3FF] flex items-center justify-center flex-shrink-0">
                            <span className="text-xs font-bold text-[#1A5EF9]">
                              {(item.username || "?").charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <span className="font-medium text-[#1A2B4A]">
                            {item.username}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">{item.email || "—"}</td>
                      <td className="px-6 py-4">{item.phoneNumber || "—"}</td>
                      <td className="px-6 py-4 max-w-[200px] truncate">
                        {item.address || "—"}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleEdit(item)}
                            className="p-1.5 rounded-[8px] text-[#6B7C98] hover:bg-[#EEF3FF] hover:text-[#1A5EF9] transition-colors"
                            title="Edit customer"
                            aria-label="Edit customer"
                          >
                            <PencilLine className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() =>
                              navigate(`/customer-history/${item.id}`)
                            }
                            className="p-1.5 rounded-[8px] text-[#1A5EF9] hover:bg-[#EEF3FF] transition-colors"
                            title="View history"
                            aria-label="View history"
                          >
                            <SquareArrowOutUpRight className="w-4 h-4" />
                          </button>
                          <Permission roles={["admin"]}>
                            <button
                              onClick={() => {
                                setDeleteId(item.id);
                                setConfirmOpen(true);
                              }}
                              className="p-1.5 rounded-[8px] text-red-400 hover:bg-red-50 transition-colors"
                              title="Delete customer"
                              aria-label="Delete customer"
                            >
                              <Trash className="w-4 h-4" />
                            </button>
                          </Permission>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <ConfirmPopup
        open={confirmOpen}
        setOpen={setConfirmOpen}
        onConfirm={() => handleDelete(deleteId)}
      />

      {/* Edit Customer Modal */}
      {editModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)] p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-5">
              <h2 className="text-lg font-semibold text-[#21314A]">
                Edit Customer
              </h2>
              <button
                onClick={() => {
                  setEditModalOpen(false);
                  setEditingCustomer(null);
                }}
                className="text-[#52627C] hover:text-[#21314A] transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-[#52627C] mb-1.5 block">
                  Name
                </label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) =>
                    setEditForm({ ...editForm, name: e.target.value })
                  }
                  placeholder="Customer name"
                  className="h-11 w-full rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-[#52627C] mb-1.5 block">
                  Phone Number
                </label>
                <input
                  type="text"
                  value={editForm.phoneNumber}
                  onChange={(e) =>
                    setEditForm({ ...editForm, phoneNumber: e.target.value })
                  }
                  placeholder="Phone number"
                  className="h-11 w-full rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-[#52627C] mb-1.5 block">
                  Email
                </label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) =>
                    setEditForm({ ...editForm, email: e.target.value })
                  }
                  placeholder="Email"
                  className="h-11 w-full rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-[#52627C] mb-1.5 block">
                  Address
                </label>
                <input
                  type="text"
                  value={editForm.address}
                  onChange={(e) =>
                    setEditForm({ ...editForm, address: e.target.value })
                  }
                  placeholder="Address"
                  className="h-11 w-full rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-[#52627C] mb-1.5 block">
                  TRN Number
                </label>
                <input
                  type="text"
                  value={editForm.trn}
                  onChange={(e) =>
                    setEditForm({ ...editForm, trn: e.target.value })
                  }
                  placeholder="TRN Number"
                  className="h-11 w-full rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-[#52627C] mb-1.5 block">
                    Credit Limit
                  </label>
                  <input
                    type="number"
                    step="any"
                    value={editForm.creditLimit}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        creditLimit: Number(e.target.value),
                      })
                    }
                    placeholder="Credit Limit"
                    className="h-11 w-full rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#52627C] mb-1.5 block">
                    Credit Period (days)
                  </label>
                  <input
                    type="number"
                    step="1"
                    value={editForm.creditPeriod}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        creditPeriod: Number(e.target.value),
                      })
                    }
                    placeholder="Credit Period"
                    className="h-11 w-full rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-[#EEF2FA]">
              <button
                type="button"
                onClick={() => {
                  setEditModalOpen(false);
                  setEditingCustomer(null);
                }}
                className="h-11 rounded-[12px] border border-[#D7E2F5] px-6 text-[#52627C] hover:bg-[#F5F8FD] font-medium text-sm transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleUpdateCustomer}
                disabled={isUpdating}
                className="h-11 rounded-[12px] bg-[#1A5EF9] px-6 text-white hover:bg-[#0E4FDE] font-medium text-sm transition-colors disabled:opacity-50"
              >
                {isUpdating ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Customer;
